import React, { useCallback, useMemo } from 'react';
import { ParentSizeModern } from '@visx/responsive';
import {
  XYChart,
  LineSeries,
  GlyphSeries,
  Axis,
  Tooltip,
  Grid,
} from '@visx/xychart';
import { Loading } from 'components';
import { scaleOrdinal } from '@visx/scale';
import type { RenderTooltipGlyphProps } from '@visx/xychart/lib/components/Tooltip';
import { NullableChartDataPoint } from '../types';
import {
  getChartMeasurement,
  getChartSeries,
  getIsolatedChartMeasurements,
} from '../utils/chartMeasurements';
import buildTheme from '../theme';
import {
  renderTooltip,
  type ChartTooltipProps,
} from '../ChartTooltip/ChartTooltip';
import { renderGlyph as renderSeriesGlyph, renderTooltipGlyph } from '../Glyph';
import calculateXNumTicks from '../utils/calculateXNumTicks';
import formatChartNumber from '../utils/formatChartNumber';
import formatTooltipDate from '../utils/formatTooltipDate';
import formatXAxisDate from '../utils/formatXAxisDate';

import './LineChart.scss';

export interface LineChartProps {
  /** Height in px */
  height?: number;
  /** Array of colors for the lines */
  colorArray?: string[];
  /** An array of objects describing the data. Each object describes the data at a point on the X-axis. */
  data: Array<NullableChartDataPoint>;
  /** Automatically prefix units (eg. k or M) */
  autoFormatYTicks?: boolean;
  /** Show show loading spinner */
  loading?: boolean;
  /** Calculate X axis `numTicks` based on chart width */
  responsiveXTicks?: boolean;
  /** Display labels for opaque series keys in tooltips. */
  seriesLabelFormatter?: (key: string) => string;
  customColorAccessors?: { [key: string]: string };
  /** Format X-axis bucket labels */
  xValueFormatter?: (value: string) => string;
  /** Format tooltip bucket labels */
  xTooltipValueFormatter?: (value: string) => string;
  yValueFormatter?: (value: number) => string;
  /** Keep sparse measurements visible even without an adjacent line segment. */
  renderGlyphs?: boolean | 'isolated';
}

const LineChart: React.FC<LineChartProps> = ({
  height,
  colorArray,
  data,
  autoFormatYTicks = true,
  loading,
  responsiveXTicks = true,
  customColorAccessors,
  xValueFormatter,
  xTooltipValueFormatter,
  seriesLabelFormatter,
  yValueFormatter,
  renderGlyphs,
}) => {
  const theme = useMemo(() => buildTheme({ colors: colorArray }), [colorArray]);

  const formattedData = useMemo(() => data.map(x => x.values), [data]);

  const labels = useMemo(() => getChartSeries(data), [data]);
  const isolatedMeasurements = useMemo(
    () =>
      new Map(
        labels.map(label => [label, getIsolatedChartMeasurements(data, label)])
      ),
    [data, labels]
  );

  const yAxisTickFormatter = useMemo(() => {
    if (autoFormatYTicks) {
      return formatChartNumber;
    }

    return undefined;
  }, [autoFormatYTicks]);

  const colorScale = useMemo(
    () =>
      customColorAccessors
        ? scaleOrdinal({
            domain: Object.keys(customColorAccessors),
            range: Object.values(customColorAccessors),
          })
        : undefined,
    [customColorAccessors]
  );

  const renderTooltipFunc = useCallback(
    (tooltipData: ChartTooltipProps) =>
      renderTooltip({
        tooltipData: tooltipData.tooltipData,
        seriesLabelFormatter,
        yValueFormatter,
        colorScale: colorScale || tooltipData.colorScale,
        formatter:
          xTooltipValueFormatter ?? xValueFormatter ?? formatTooltipDate,
      }),
    [
      colorScale,
      seriesLabelFormatter,
      yValueFormatter,
      xTooltipValueFormatter,
      xValueFormatter,
    ]
  );

  const renderGlyph = useCallback(
    (tooltipData: RenderTooltipGlyphProps<NullableChartDataPoint['values']>) =>
      renderTooltipGlyph({
        ...tooltipData,
        color: customColorAccessors
          ? customColorAccessors[tooltipData.key]
          : tooltipData.color,
      }),
    [customColorAccessors]
  );

  return (
    <ParentSizeModern className="graph-container" debounceTime={10}>
      {({ width: visWidth, height: visHeight }) => {
        const xNumTicks = responsiveXTicks
          ? calculateXNumTicks(visWidth, formattedData)
          : undefined;

        return (
          <>
            <Loading visible={loading} maskContainer />
            <XYChart
              width={visWidth}
              height={height ?? visHeight}
              xScale={{ type: 'band' }}
              yScale={{ type: 'linear' }}
              theme={theme}
              margin={{ top: 10, right: 40, left: 50, bottom: 40 }}
            >
              <Axis
                orientation="bottom"
                numTicks={xNumTicks}
                tickFormat={xValueFormatter ?? formatXAxisDate}
              />
              <Axis
                orientation="left"
                strokeWidth={0}
                tickFormat={yAxisTickFormatter}
              />
              <Grid rows columns={false} />
              {labels.map(label => (
                <LineSeries
                  key={label}
                  dataKey={label}
                  data={formattedData}
                  xAccessor={d => d?.name}
                  yAccessor={d => getChartMeasurement(d, label)}
                  colorAccessor={
                    customColorAccessors
                      ? dataKey => customColorAccessors[dataKey]
                      : undefined
                  }
                />
              ))}
              {renderGlyphs &&
                labels.map(label => (
                  <GlyphSeries
                    key={`${label}-glyph`}
                    dataKey={label}
                    data={formattedData}
                    xAccessor={d => d.name}
                    yAccessor={d => getChartMeasurement(d, label)}
                    size={renderGlyphs === 'isolated' ? 4 : undefined}
                    renderGlyph={glyph =>
                      renderGlyphs !== 'isolated' ||
                      isolatedMeasurements.get(label)?.has(glyph.datum)
                        ? renderSeriesGlyph({
                            ...glyph,
                            color: customColorAccessors?.[label] ?? glyph.color,
                          })
                        : null
                    }
                  />
                ))}
              <Tooltip
                snapTooltipToDatumX
                snapTooltipToDatumY
                showVerticalCrosshair
                showSeriesGlyphs
                unstyled
                applyPositionStyle
                renderTooltip={renderTooltipFunc}
                renderGlyph={renderGlyph}
              />
            </XYChart>
          </>
        );
      }}
    </ParentSizeModern>
  );
};

export default LineChart;
